import styled from '@emotion/styled';
import { Paper, useMediaQuery } from '@material-ui/core';
import { createElement, FC, useMemo } from 'react';
import { FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import { ImprovementValue, ImprovementValues, PerformanceReportMetric } from '../../../../generated/graphql';
import { calcImprovementRate } from '../../../../utility/extractPeriodicImprovementPerformance';
import roundDecimalPoint from '../../../../utility/roundDecimalPoint';
import { SPACING_HUGE, SPACING_UNIT } from '../../../constant/spacing';
import { breakpoint } from '../../../styleUtility/mediaquery';
import RechartsResponsiveContainer from '../../primitive/RechartsResponsiveContainer';

const barColors = ['#00BCD4', '#FFC107', '#4CAF50'];
const LABEL_FONT_SIZE = 10;

type Domain = [number, number];

type LabelProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  value: any;
};

type ExtendedLabelProps = {
  target: string;
  intl: InjectedIntl;
} & LabelProps;

type Target = keyof ImprovementValues;

type TargetValues = {
  all?: number;
  video?: number;
  staticImage?: number;
};

type BarData = {
  metric?: string;
} & TargetValues;

const targets: Target[] = ['all', 'video', 'staticImage'];
const metrics: PerformanceReportMetric[] = [
  'impressions',
  'click_through_rate',
  'cost_per_click',
  'cost_per_action',
  'view_through_cost_per_action',
];

const camelToSnake = (str: string) => {
  return str
    .split(/(?=[A-Z])/)
    .join('_')
    .toLowerCase();
};

const getTargetValues = (metric: string, data: ImprovementValues) => {
  const output: TargetValues = {};
  targets.forEach((target: Target) => {
    const foundMetric = data[target].find((el: ImprovementValue) => el.metric === metric);

    if (foundMetric && foundMetric.actualValue && foundMetric.targetValue) {
      const improvementRate = calcImprovementRate(foundMetric.actualValue, foundMetric.targetValue);
      if (improvementRate) output[target] = roundDecimalPoint(improvementRate * 100, 2);
    }
  });
  return output;
};

const getChartData = (data: ImprovementValues) => {
  const barData: BarData[] = metrics.map((metric) => {
    const targetsValues = getTargetValues(metric, data);
    return { metric, ...targetsValues };
  });
  return barData;
};

const formatXAxis = (tick: string, intl: InjectedIntl) =>
  intl.formatMessage({ id: `performance_report_metric.${tick}` });

const formatTooltip = (value: string | number | Array<string | number>, name: string, intl: InjectedIntl) => [
  typeof value === 'string' || typeof value === 'number' ? `${value}%` : value,
  intl.formatMessage({ id: `performance_report_card.options.comparison_target_key.${camelToSnake(name)}` }),
];

const renderDomain = (data: BarData[]): Domain => {
  // FIXME: flatMap で書き直す
  // 現在のpolyfill だとMicrosoft Edge (Legacy) 環境でエラーになってしまう
  // https://github.com/kaizenplatform/hugot-director-webclient/issues/1512
  const mappedData = data.map((item) => Object.values(item));
  const values: number[] = [];
  mappedData.forEach((barData) => {
    const filtered = barData.filter((value) => typeof value === 'number') as number[];
    filtered.forEach((value) => values.push(value));
  });

  const uniqueValues = [...new Set(values)];
  const absoluteValues = uniqueValues.map((value: number) => Math.abs(value));
  const maxValue = Math.max(...absoluteValues);
  const domainValue = Math.ceil(maxValue / 10) * 10;
  return [-domainValue, domainValue];
};

const FormatLabel = (props: ExtendedLabelProps) => {
  const { x, y, width, height, value, target, intl } = props;
  if (!value) return null;

  const isPositiveValue = Math.sign(value) === 1;
  return (
    <g>
      <text
        x={x + width / 2}
        y={y + height / 2 + 8}
        fontSize={LABEL_FONT_SIZE}
        fill="rgba(0, 0, 0, 0.87)"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {(isPositiveValue && '+') + value + '%'}
      </text>
      <text
        x={x + width / 2}
        y={y + height / 2 - 8}
        fontSize={LABEL_FONT_SIZE}
        fill="rgba(0, 0, 0, 0.87)"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {intl.formatMessage({ id: `performance_report_card.options.comparison_target_key.${camelToSnake(target)}` })}
      </text>
    </g>
  );
};

type Props = {
  periodicImprovementValues: ImprovementValues;
  className?: string;
} & InjectedIntlProps;

const ImprovementBarChart: FC<Props> = ({ periodicImprovementValues, intl, className }) => {
  const data = useMemo(() => getChartData(periodicImprovementValues), [periodicImprovementValues]);
  const isSmartPhoneScreenWidth = useMediaQuery(`(max-width:${breakpoint.sp}px)`);
  const marginRight = isSmartPhoneScreenWidth ? 20 : 40;
  return (
    <Root className={className}>
      <RechartsResponsiveContainer width="100%" height={350}>
        <BarChart
          data={data}
          margin={{ left: 12, right: marginRight, bottom: 30 }}
          barGap="1%"
          style={{
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: 14,
          }}
        >
          <XAxis
            dataKey="metric"
            tickMargin={SPACING_UNIT / 2}
            tickFormatter={(tick) => formatXAxis(tick, intl)}
            interval={0}
          />
          <YAxis scale="sqrt" unit="%" domain={renderDomain(data)} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            cursor={false}
            formatter={(value, name) => formatTooltip(value, name, intl)}
            labelFormatter={(value) => (
              <FormattedMessage id={`performance_report_metric.${camelToSnake(value as string)}`} />
            )}
          />
          <Legend
            formatter={(value) => (
              <FormattedMessage id={`performance_report_card.options.comparison_target_key.${camelToSnake(value)}`} />
            )}
            wrapperStyle={{ left: 20, right: 20, bottom: 20, lineHeight: 1.7 }}
          />
          {targets.map((target, index) => (
            <Bar
              key={target}
              dataKey={target}
              fill={barColors[index % barColors.length]}
              label={
                !isSmartPhoneScreenWidth &&
                ((props: LabelProps) => <FormatLabel {...props} target={target} intl={intl} />)
              }
            />
          ))}
        </BarChart>
      </RechartsResponsiveContainer>
    </Root>
  );
};

const Root = styled(Paper)({
  paddingTop: SPACING_HUGE,
});

export default injectIntl(ImprovementBarChart);
